// Matter.js physics world + static walls/floor.
// No Matter.Render — we draw bodies ourselves.

import Matter from 'matter-js';

export type PhysicsWorld = {
  engine: Matter.Engine;
  walls: Matter.Body[];
  resize(w: number, h: number): void;
};

const WALL_THICKNESS = 300;

export function createPhysicsWorld(width: number, height: number): PhysicsWorld {
  const engine = Matter.Engine.create({
    gravity: { x: 0, y: 1, scale: 0.0014 },
    // Sleep settled bodies so a large pile stops burning CPU.
    // cameraMotion / devicemotion / MouseConstraint wake them on interaction.
    enableSleeping: true,
    // Deep stacks of circles need enough solver iterations to resolve contact
    // overlap each tick — lowering these caused sleeping bodies to wake with
    // a jolt and launch out of the pile.
    positionIterations: 8,
    velocityIterations: 8,
  });

  const floor = Matter.Bodies.rectangle(
    width / 2,
    height + WALL_THICKNESS / 2,
    width * 3, // extra wide so tilts don't let letters slip past corners
    WALL_THICKNESS,
    { isStatic: true, friction: 0.8, restitution: 0.1, label: 'floor' },
  );
  const leftWall = Matter.Bodies.rectangle(
    -WALL_THICKNESS / 2,
    height / 2,
    WALL_THICKNESS,
    height * 3,
    { isStatic: true, friction: 0.5, restitution: 0.1, label: 'wall-left' },
  );
  const rightWall = Matter.Bodies.rectangle(
    width + WALL_THICKNESS / 2,
    height / 2,
    WALL_THICKNESS,
    height * 3,
    { isStatic: true, friction: 0.5, restitution: 0.1, label: 'wall-right' },
  );
  // Ceiling to prevent letters from being pushed off-top during shakes.
  const ceiling = Matter.Bodies.rectangle(
    width / 2,
    -WALL_THICKNESS / 2,
    width * 3,
    WALL_THICKNESS,
    { isStatic: true, friction: 0.5, restitution: 0.1, label: 'ceiling' },
  );

  const walls = [floor, leftWall, rightWall, ceiling];
  Matter.World.add(engine.world, walls);

  return {
    engine,
    walls,
    resize(w: number, h: number) {
      Matter.Body.setPosition(floor, { x: w / 2, y: h + WALL_THICKNESS / 2 });
      Matter.Body.setPosition(leftWall, { x: -WALL_THICKNESS / 2, y: h / 2 });
      Matter.Body.setPosition(rightWall, { x: w + WALL_THICKNESS / 2, y: h / 2 });
      Matter.Body.setPosition(ceiling, { x: w / 2, y: -WALL_THICKNESS / 2 });
    },
  };
}

// Matter.js only wakes sleeping bodies on *active* collision — a body that
// loses its support when a neighbor moves away stays asleep in mid-air. Call
// this after any interaction that reshuffles the pile (new drop, drag start)
// so former-neighbors re-feel gravity and re-settle.
export function wakeAllBodies(engine: Matter.Engine): void {
  for (const body of Matter.Composite.allBodies(engine.world)) {
    if (!body.isStatic && body.isSleeping) Matter.Sleeping.set(body, false);
  }
}
